import { OrderStatus } from '@/compiled_proto/com/celertech/orderrouting/api/enums/OrderStatusProto';
import { OrderType } from '@/compiled_proto/com/celertech/positionmanager/api/enums/OrderTypeProto';
import ContextMenu, { ContextMenuItem } from '@/components/common/ContextMenu';
import TabFilters from '@/components/common/TabFilters';
import LuceraAmendOcoOrderModal from '@/components/modal/LuceraAmendOcoOrderModal';
import LuceraAmendOrderModal from '@/components/modal/LuceraAmendOrderModal';
import LuceraDealHistoryModal from '@/components/modal/LuceraDealHistoryModal';
import OrderCancellationModal from '@/components/modal/OrderCancellationModal';
import VirtualizedTable from '@/components/table/VirtualizedTable';
import { convertedQtyFormatterConfig, defaultColumn } from '@/config/config';
import { orderColumns } from '@/helpers/table/luceraOrderBlotterTableHelper';
import { LuceraBlotterItem } from '@/model/blotters';
import { Modify } from '@/model/common';
import { useAppSelector } from '@/state/hooks';
import { selectCurrentAccount } from '@/state/reducers/authSlice';
import { selectLuceraBlotterItems } from '@/state/reducers/blotterSlice';
import { selectGeneralSettings } from '@/state/reducers/settingSlice';
import { LumeFxOrderStatus, LumeFxOrderType, LumeFxTimeInForce } from '@/types/lumeFx';
import useContextMenu from '@/utils/hooks/useContextMenu';
import { useDisclosure } from '@/utils/hooks/useDisclosure';
import { getInstrumentFormatter, getUniversalInstrument } from '@/utils/hooks/useInstrument';
import useLanguage from '@/utils/hooks/useLanguage';
import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import { translatePair } from '@/utils/symbolMapping';
import {
    ExpandedState,
    SortingState,
    getCoreRowModel,
    getExpandedRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable
} from '@tanstack/react-table';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocale, useNumberFormatter } from 'react-aria';
import { MdCancel, MdEdit, MdHistory } from 'react-icons/md';

export interface OrderBlotterRow
    extends Modify<
        LuceraBlotterItem,
        {
            qty: string;
            price: string;
            lastPrice: string;
            tableStopPx: string;
        }
    > {}

const validateAmendableOrder = (order: OrderBlotterRow) => {
    const tif = LumeFxTimeInForce[order.tif] as LumeFxTimeInForce;
    const status = LumeFxOrderStatus[order.status] as LumeFxOrderStatus;
    const orderType = LumeFxOrderType[order.orderType] as LumeFxOrderType;
    // if duration is DAY/GTC && if order status is NEW/PART_FILLED

    const amendable =
        [LumeFxTimeInForce.DAY, LumeFxTimeInForce.GTC].includes(tif) &&
        [LumeFxOrderStatus.New, LumeFxOrderStatus.PartiallyFilled].includes(status) &&
        [LumeFxOrderType.Limit, LumeFxOrderType.Stop, LumeFxOrderType.StopLimit].includes(orderType);

    return amendable;
};

const getLinkedOrder = (e: RowEvent, orders: LuceraBlotterItem[]) => {
    const ocoOrders = orders.filter((order) => {
        return order.clOrdLinkId === e.original.clOrdLinkId;
    });

    const linkedOrder = (ocoOrders?.filter((order) => order.orderId !== e.original.orderId) || [])[0];
    return linkedOrder;
};

type RowEvent = { event: MouseEvent; original: OrderBlotterRow };

export type PositionFilter = 'Show All' | 'Filled' | 'Open' | 'Cancelled' | 'Rejected';

interface LuceraOrderBlotterTableProps {
    setCSVData: React.Dispatch<React.SetStateAction<any[]>>;
}

const LuceraOrderBlotterTable = (props: LuceraOrderBlotterTableProps) => {
    const screen = useScreenSizes();

    const lang = useLanguage();
    const orders = useAppSelector(selectLuceraBlotterItems);

    const amendDisclosure = useDisclosure(false);
    const amendOcoDisclosure = useDisclosure(false);
    const historyDisclosure = useDisclosure(false);
    const cancelDisclosure = useDisclosure(false);

    const { contextMenuX, contextMenuY, contextMenuVisible, handleOpenContextMenu, handleCloseContextMenu } =
        useContextMenu();

    const [orderId, setOrderId] = useState<string | undefined>();
    const [linkedOrderId, setLinkedOrderId] = useState<string | undefined>();
    const [cancelOrder, setCancelOrder] = useState<LuceraBlotterItem | undefined>();
    const [cancelOrder2, setCancelOrder2] = useState<LuceraBlotterItem | undefined>();
    const [isAmendable, setIsAmendable] = useState<boolean>(false);
    const [isAmendOco, setIsAmendOco] = useState<boolean>(false);
    const [positionFilter, setPositionFilter] = useState<PositionFilter>('Show All');

    const { order, linkedOrder, tableInstance } = useLuceraOrderBlotterTable({
        ...props,
        orderId,
        linkedOrderId,
        positionFilter,
        amendDisclosure,
        amendOcoDisclosure,
        handleOpenContextMenu
    });

    const positionFilters: { label: string; value: PositionFilter }[] = useMemo(() => {
        return [
            { label: lang.blotters.showAll, value: 'Show All' },
            // (Filled and Partially Filled)
            { label: lang.blotters.filled, value: 'Filled' },
            // (New and Partially Filled)
            { label: lang.blotters.open, value: 'Open' },
            // (Canceled)
            { label: lang.blotters.cancelled, value: 'Cancelled' }
            // // (Rejected)
            // { label: lang.blotters.rejected, value: 'Rejected' }
        ];
    }, [lang]);

    const onAction = useCallback(
        (e: RowEvent, callback: () => void) => {
            const { original } = e;
            let amendable = false;
            const isOCO = original.clOrdLinkId !== undefined;

            if (original) amendable = validateAmendableOrder(original);

            if (isOCO) {
                const linkedOrder = getLinkedOrder(e, orders);
                if (linkedOrder) {
                    setIsAmendOco(true);
                    setOrderId(original.orderId);
                    setLinkedOrderId(linkedOrder.orderId);
                    setIsAmendable(amendable);
                    callback();
                } else {
                    setIsAmendOco(false);
                    setOrderId(original.orderId);
                    setIsAmendable(amendable);
                    callback();
                }
            } else {
                setIsAmendOco(false);
                setOrderId(original.orderId);
                setLinkedOrderId(undefined);
                setIsAmendable(amendable);
                callback();
            }
        },
        [orders]
    );

    return (
        <Fragment>
            <div className="flex flex-col h-full gap-2">
                <TabFilters.Common
                    filters={positionFilters}
                    selectedFilter={positionFilter}
                    setSelectedFilter={setPositionFilter}
                />
                <div className="flex-1 basis-0 overflow-hidden">
                    <VirtualizedTable
                        tableInstance={tableInstance}
                        onCellContextMenu={(e: RowEvent) => onAction(e, () => handleOpenContextMenu(e.event as any))}
                        onRowDoubleClicked={(e: RowEvent) =>
                            onAction(e, () => {
                                if (isAmendOco) amendOcoDisclosure[1].toggle();
                                else if (isAmendable) amendDisclosure[1].toggle();
                            })
                        }
                        onRowClicked={(e: RowEvent) => {
                            if (screen.mobile || screen.tablet)
                                onAction(e, () => handleOpenContextMenu(e.event as any));
                        }}
                        isLoadingTableContent={false}
                    />
                </div>
            </div>
            <ContextMenu
                x={contextMenuX}
                y={contextMenuY}
                visible={contextMenuVisible}
                onClose={handleCloseContextMenu}>
                <ContextMenuItem onClick={historyDisclosure[1].open}>
                    <MdHistory />
                    <span>{lang.blotters.viewOrderHistory}</span>
                </ContextMenuItem>
                <ContextMenuItem
                    onClick={() => {
                        if (isAmendOco) amendOcoDisclosure[1].open();
                        else amendDisclosure[1].open();
                    }}
                    disabled={!isAmendable}>
                    <MdEdit />
                    <span>{lang.blotters.amendOrder}</span>
                </ContextMenuItem>
                <ContextMenuItem
                    mode="secondary"
                    onClick={() => {
                        const order = orders.find((order) => order.orderId === orderId);
                        const order2 = linkedOrderId
                            ? orders.find((order) => order.orderId === linkedOrderId)
                            : undefined;
                        setCancelOrder(order);
                        if (order2) setCancelOrder2(order2);
                        else setCancelOrder2(undefined);
                        cancelDisclosure[1].open();
                    }}
                    disabled={!isAmendable}>
                    <MdCancel />
                    <span>{lang.orderForm.cancelOrder}</span>
                </ContextMenuItem>
            </ContextMenu>
            {order && (
                <>
                    {linkedOrder && (
                        <LuceraAmendOcoOrderModal
                            listId={order.clOrdLinkId || ''}
                            orders={{
                                order1: +order.orderId > +linkedOrder.orderId ? linkedOrder : order,
                                order2: +order.orderId > +linkedOrder.orderId ? order : linkedOrder
                            }}
                            duration={order.tif}
                            opened={amendOcoDisclosure[0]}
                            handlers={amendOcoDisclosure[1]}
                            onCancelOrder={(orderToCancel, order2ToCancel) => {
                                setCancelOrder(orderToCancel);
                                setCancelOrder2(order2ToCancel);
                                amendOcoDisclosure[1].close();
                                cancelDisclosure[1].open();
                            }}
                        />
                    )}
                    <LuceraDealHistoryModal
                        order={order}
                        linkedOrder={linkedOrder}
                        opened={historyDisclosure[0]}
                        handlers={historyDisclosure[1]}
                    />
                    <LuceraAmendOrderModal
                        order={order}
                        opened={amendDisclosure[0]}
                        handlers={amendDisclosure[1]}
                        onCancelOrder={(orderToCancel) => {
                            setCancelOrder(orderToCancel);
                            setCancelOrder2(undefined);
                            amendDisclosure[1].close();
                            cancelDisclosure[1].open();
                        }}
                    />
                    {cancelOrder && (
                        <OrderCancellationModal
                            order1={cancelOrder}
                            order2={cancelOrder2}
                            opened={cancelDisclosure[0]}
                            handlers={cancelDisclosure[1]}
                        />
                    )}
                </>
            )}
        </Fragment>
    );
};

export default LuceraOrderBlotterTable;

export const mapOrderBlotters = (order: LuceraBlotterItem, locale: string, formatNumber: any): OrderBlotterRow => {
    const { formatPrice, formatAvgPrice } = getInstrumentFormatter(locale, order.pair);
    const isStopMarket = OrderType[order.orderType] === OrderType.STOP_MARKET;

    let price;
    let tableInstrument = translatePair(order.pair);
    const instrumentConfig = getUniversalInstrument(order.pair);

    if (instrumentConfig?.type === 'Future') tableInstrument = instrumentConfig.gcexSymbol;
    if (isStopMarket) price = '-';
    else {
        if (order.price) price = formatPrice(order.price);
        else price = '-';
    }

    return {
        ...order,
        pair: tableInstrument,
        qty: order.qty ? formatNumber.format(order.qty) : '-',
        cumQty: order.cumQty ? formatNumber.format(order.cumQty) : '-',
        lastQty: order.lastQty ? formatNumber.format(order.lastQty) : '-',
        lastPrice: order.lastPrice ? formatPrice(order.lastPrice) : '-',
        tableStopPx: order.stopPx ? formatPrice(order.stopPx) : '-',
        price
        // “Filled Qty”/”Ord Qty”
        // pctFilled:
        //     order.qty > 0
        //         ? BigNumber(order.cumQty).dividedBy(order.qty).multipliedBy(100).decimalPlaces(0).toNumber() + '%'
        //         : '-',
    };
};

interface UseLuceraOrderBlotterTableProps extends LuceraOrderBlotterTableProps {
    orderId: string | undefined;
    linkedOrderId: string | undefined;
    positionFilter: PositionFilter;
    amendDisclosure: ReturnType<typeof useDisclosure>;
    amendOcoDisclosure: ReturnType<typeof useDisclosure>;
    handleOpenContextMenu: (row: any, x: number, y: number) => void;
}

const useLuceraOrderBlotterTable = (props: UseLuceraOrderBlotterTableProps) => {
    const { orderId, linkedOrderId, positionFilter, amendDisclosure, amendOcoDisclosure, setCSVData } = props;

    const { locale } = useLocale();
    const orders = useAppSelector(selectLuceraBlotterItems);
    const currentAccount = useAppSelector(selectCurrentAccount);
    const generalSettings = useAppSelector(selectGeneralSettings);

    const lang = useLanguage();

    const formatNumber = useNumberFormatter(convertedQtyFormatterConfig);

    const [sorting, setSorting] = useState<SortingState>([{ id: 'updated', desc: true }]);

    // const [debouncedOrderBlotters, setDebouncedOrderBlotters] = useState<LuceraBlotterItem[]>([]);
    const [expandedRows, setExpandedRows] = useState<ExpandedState>({});

    const order = useMemo(() => orders.find((order) => order.orderId === orderId), [orders, orderId]);
    const linkedOrder = useMemo(() => orders.find((order) => order.orderId === linkedOrderId), [orders, linkedOrderId]);

    const memoizedOrder = useMemo(() => order, [order, amendDisclosure[0], amendOcoDisclosure[0]]);
    const memoizedLinkedOrder = useMemo(() => linkedOrder, [linkedOrder, amendDisclosure[0], amendOcoDisclosure[0]]);

    const formattedOrders: any = useMemo(() => {
        // const currentAccountOrders = orders.filter(
        //     (order) => order.account === currentAccount || order.account === currentAccount?.slice(0, 36)
        // );
        const filteredOrders = orders.filter((order) => {
            if (positionFilter === 'Show All') return true;
            if (positionFilter === 'Filled') {
                const isFilled = [LumeFxOrderStatus.Filled, LumeFxOrderStatus.PartiallyFilled].includes(
                    OrderStatus[order.status]
                );
                const isCancelled = LumeFxOrderStatus.Canceled === OrderStatus[order.status] && order.cumQty > 0;

                return isFilled || isCancelled;
            }
            if (positionFilter === 'Open') {
                return [LumeFxOrderStatus.New, LumeFxOrderStatus.PartiallyFilled].includes(OrderStatus[order.status]);
            }
            if (positionFilter === 'Cancelled') {
                // LumeFxOrderStatus.REJECTED
                return [LumeFxOrderStatus.Canceled].includes(OrderStatus[order.status]);
            }
            // if (positionFilter === 'Rejected') {
            //     // LumeFxOrderStatus.REJECTED
            //     return [LumeFxOrderStatus.Replaced].includes(OrderStatus[order.status]);
            // }
            return true;
        });

        filteredOrders.sort((a, b) => {
            if (+a.orderId < +b.orderId) return -1;
            if (+a.orderId > +b.orderId) return 1;
            return 0;
        });

        const orderBlotters = filteredOrders.map((order) => mapOrderBlotters(order, locale, formatNumber));
        // const groupedOrders = groupRowsByParentStrategyId(orderBlotters);

        return orderBlotters;
    }, [orders, locale, currentAccount, positionFilter, generalSettings]);

    const columns = useMemo(() => orderColumns(lang, generalSettings), [lang, generalSettings]);

    const tableInstance = useReactTable({
        data: formattedOrders,
        columns,
        state: { sorting, expanded: expandedRows },
        defaultColumn,
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        globalFilterFn: 'includesString',
        onSortingChange: (sorting) => setSorting(sorting),
        onExpandedChange: setExpandedRows,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        getExpandedRowModel: getExpandedRowModel(),
        getSubRows: (row: any) => row.subRows,
        enableRowSelection: true
    });

    // const [debouncedTableInstance] = useDebouncedValue(tableInstance, 5000);

    useEffect(() => {
        setCSVData(formattedOrders);
    }, [formattedOrders]);

    useEffect(() => {
        tableInstance.toggleAllRowsExpanded();
    }, []);

    // useInterval(
    //     () => {
    //         setDebouncedOrderBlotters(store.getState().);
    //     },
    //     5000,
    //     true
    // );

    return { order: memoizedOrder, linkedOrder: memoizedLinkedOrder, tableInstance, formattedOrders };
};
